import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { capabilities } from '../data/mockData';
import { ArrowRight, CheckCircle2, Mail } from 'lucide-react';

export const CapabilityDetail = () => {
  const { slug } = useParams();
  const capability = capabilities.find(c => c.slug === slug);

  if (!capability) {
    return (
      <div className="min-h-screen pt-32 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-bold mb-4">Capability Not Found</h1>
          <Link to="/"><Button>Return Home</Button></Link>
        </div>
      </div>
    );
  }

  const materials = {
    'cnc-machining': {
      metals: ['Aluminum alloys (6061, 7075, 2024)', 'Stainless steel (304, 316, 17-4 PH)', 'Carbon steel and alloy steels', 'Brass and copper alloys'],
      plastics: ['Acetal (Delrin)', 'PEEK and PEI', 'Nylon and glass-filled nylons', 'ABS and polycarbonate']
    },
    'injection-molding': {
      plastics: ['ABS', 'Polycarbonate (PC)', 'Polypropylene (PP)', 'Polyethylene (PE)', 'Nylon (PA)', 'TPE/TPU', 'POM (Acetal)', 'PEEK']
    },
    'die-casting': {
      metals: ['Aluminum alloys (A380, A383, A413)', 'Zinc alloys (Zamak 3, Zamak 5)', 'Magnesium alloys']
    },
    'sheet-metal': {
      metals: ['Cold Rolled Steel', 'Aluminum (5052, 6061)', 'Stainless Steel (304, 316)', 'Galvanized Steel', 'Brass and Copper']
    },
    'aluminum-extrusion': {
      metals: ['6063 Aluminum (standard)', '6061 Aluminum (higher strength)', '7075 Aluminum (high performance)']
    },
    'assembly': {
      components: ['Mechanical assembly', 'Hardware installation', 'Adhesive bonding', 'Welding and fastening', 'Testing and inspection']
    }
  };

  const volumeRanges = [
    { label: 'Prototype', range: '1-50 pieces', desc: 'Design validation and testing' },
    { label: 'Low Volume', range: '50-500 pieces', desc: 'Pilot production and market testing' },
    { label: 'Production', range: '500+ pieces', desc: 'Full-scale manufacturing' }
  ];

  const qualityApproach = [
    'Setup Inspection: First article measurement and approval',
    'In-Process Control: Statistical monitoring of critical dimensions',
    'Final Inspection: CMM verification and dimensional reports',
    'Documentation: Detailed inspection reports with traceability'
  ];

  const secondaryOperations = {
    'cnc-machining': ['Anodizing and hard coat', 'Electroplating and chemical film', 'Heat treatment', 'Assembly and sub-assembly'],
    'injection-molding': ['Pad printing and labeling', 'Assembly and overmolding', 'Surface texturing', 'Insert molding'],
    'die-casting': ['Machining and tapping', 'Powder coating', 'Plating', 'Assembly'],
    'sheet-metal': ['Powder coating', 'Plating (zinc, chrome)', 'Welding and assembly', 'Hardware installation'],
    'aluminum-extrusion': ['CNC machining', 'Anodizing', 'Powder coating', 'Assembly'],
    'assembly': ['Testing and inspection', 'Packaging and labeling', 'Quality documentation', 'Kitting and logistics']
  };

  const currentMaterials = materials[slug] || {};
  const currentSecondary = secondaryOperations[slug] || [];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 z-0">
          <img 
            src={capability.image} 
            alt={capability.title} 
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/80"></div>
        </div>

        <div className="max-w-4xl mx-auto relative z-10">
          <Badge className="mb-4">{capability.category}</Badge>
          <h1 className="text-5xl font-bold text-foreground mb-6">
            {capability.title} Services
          </h1>
          <p className="text-xl text-muted-foreground mb-8">
            From prototype to production volumes, we deliver precise components with complete documentation and quality control.
          </p>
        </div>
      </section>

      {/* What We Manufacture */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">What We Manufacture</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-6">
                <ul className="space-y-2">
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                    <span>Precision brackets and mounting systems</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                    <span>Complex housings and enclosures</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                    <span>Custom mechanical components</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <ul className="space-y-2">
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                    <span>Prototype parts for design validation</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                    <span>Production assemblies</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                    <span>High-precision components</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Materials */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">Materials We Work With</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {currentMaterials.metals && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold mb-4">Metals</h3>
                  <ul className="space-y-2">
                    {currentMaterials.metals.map((material, index) => (
                      <li key={index} className="flex items-start space-x-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                        <span>{material}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
            {currentMaterials.plastics && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold mb-4">Plastics</h3>
                  <ul className="space-y-2">
                    {currentMaterials.plastics.map((material, index) => (
                      <li key={index} className="flex items-start space-x-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                        <span>{material}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
            {currentMaterials.components && (
              <Card className="md:col-span-2">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold mb-4">Services</h3>
                  <ul className="grid md:grid-cols-2 gap-2">
                    {currentMaterials.components.map((material, index) => (
                      <li key={index} className="flex items-start space-x-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                        <span>{material}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Tolerances & Volume */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div>
              <h2 className="text-3xl font-bold mb-6">Tolerances & Specifications</h2>
              <p className="text-muted-foreground mb-4">
                Tolerance requirements are reviewed during the RFQ process based on your specific application and drawing requirements. We maintain capability for precision work requiring tight geometric controls.
              </p>
              <p className="text-sm text-muted-foreground italic">
                Standard tolerances and achievable precision discussed during quote process
              </p>
            </div>

            <div>
              <h2 className="text-3xl font-bold mb-6">Typical Volume Ranges</h2>
              <div className="space-y-3">
                {volumeRanges.map((range, index) => (
                  <Card key={index}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-bold">{range.label}</span>
                        <Badge variant="outline">{range.range}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{range.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Approach */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">Quality Approach for {capability.title}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {qualityApproach.map((item, index) => (
              <Card key={index}>
                <CardContent className="p-4">
                  <p className="text-sm">{item}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Secondary Operations */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">Secondary Operations</h2>
          <p className="text-muted-foreground mb-6">Available through qualified partners:</p>
          <div className="grid md:grid-cols-2 gap-4">
            {currentSecondary.map((operation, index) => (
              <div key={index} className="flex items-center space-x-2">
                <CheckCircle2 className="h-5 w-5 text-accent flex-shrink-0" />
                <span>{operation}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Start Your {capability.title} Project?</h2>
          <p className="text-xl mb-8 text-primary-foreground/90">
            Get your RFQ response within 24-48 hours
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground">
                Request Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href="mailto:rfq@allpartsmanufacturing.com">
              <Button size="lg" variant="outline" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                <Mail className="mr-2 h-5 w-5" />
                Email RFQ
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
